import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  clearCurrent,
  createEvaluationThunk,
  loadEvaluations,
  loadSupplier,
} from '../features/suppliers/suppliersSlice';
import type { SupplierStatus } from '../features/suppliers/types';
import { RootState, AppDispatch } from '../store/store';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { DateCell } from '../components/DateCell';
import { AttachmentsList } from '../components/AttachmentsList';
import {
  REEVALUATION_STATE_CLASS,
  REEVALUATION_STATE_LABEL,
  resolveReevaluationState,
} from '../features/suppliers/supplierReevaluationUtils';

const STATUS_LABEL: Record<SupplierStatus, string> = {
  apto: 'Apto',
  no_apto: 'No apto',
  suspendido: 'Suspendido',
  baja: 'Baja',
};

const evalSchema = z.object({
  evaluatedAt: z.string().min(1, 'Requerido'),
  score: z.coerce.number().min(0).max(100),
  notes: z.string().trim().max(5000).optional(),
});
type EvalForm = z.infer<typeof evalSchema>;

export function SupplierDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const { canEdit } = usePlatformAccessLevel('suppliers.access');
  const { current: supplier, evaluations, evalsLoading } = useSelector((s: RootState) => s.suppliers);

  useEffect(() => {
    if (!id) return;
    dispatch(loadSupplier(id));
    dispatch(loadEvaluations(id));
    return () => {
      dispatch(clearCurrent());
    };
  }, [id, dispatch]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EvalForm>({
    resolver: zodResolver(evalSchema),
    defaultValues: { evaluatedAt: '', score: 80, notes: '' },
  });

  const onSubmit = useCallback(
    async (values: EvalForm) => {
      await dispatch(
        createEvaluationThunk({
          supplierId: id,
          payload: {
            evaluatedAt: new Date(values.evaluatedAt).toISOString(),
            score: values.score,
            notes: values.notes ? values.notes : null,
          },
        }),
      ).unwrap();
      reset({ evaluatedAt: '', score: 80, notes: '' });
    },
    [dispatch, id, reset],
  );

  const handleAddEval = handleSubmit(onSubmit);

  if (!supplier) {
    return <p className="text-muted-foreground">Cargando proveedor…</p>;
  }

  const reevaluationState = resolveReevaluationState(supplier.nextReevaluationAt);

  return (
    <div className="space-y-6">
      <div>
        <Link to="/proveedores" className="text-sm text-muted-foreground hover:underline">
          ← Volver a Proveedores
        </Link>
        <h1 className="text-2xl font-semibold mt-2">{supplier.legalName}</h1>
        <p className="text-sm text-muted-foreground">
          Estado: <strong>{STATUS_LABEL[supplier.status]}</strong>
          {supplier.taxId && (
            <>
              {' · '}CUIT: {supplier.taxId}
            </>
          )}
          {supplier.category && (
            <>
              {' · '}Categoría: {supplier.category}
            </>
          )}
        </p>
      </div>

      <section className="grid md:grid-cols-2 gap-4 text-sm">
        <Card title="Contacto">
          <Row label="Nombre" value={supplier.contactName} />
          <Row label="Email" value={supplier.contactEmail} />
          <Row label="Teléfono" value={supplier.contactPhone} />
          <Row label="Dirección" value={supplier.address} />
        </Card>
        <Card title="Información">
          <Row label="Aprobado" value={supplier.approvedAt ? <DateCell value={supplier.approvedAt} /> : null} />
          <Row
            label="Frecuencia reevaluación"
            value={
              supplier.reevaluationFrequencyMonths
                ? `${supplier.reevaluationFrequencyMonths} mes(es)`
                : null
            }
          />
          <Row
            label="Próxima reevaluación"
            value={
              supplier.nextReevaluationAt ? (
                <span className="inline-flex flex-wrap items-center gap-2">
                  <DateCell value={supplier.nextReevaluationAt} />
                  {reevaluationState && (
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs ${REEVALUATION_STATE_CLASS[reevaluationState]}`}
                    >
                      {REEVALUATION_STATE_LABEL[reevaluationState]}
                    </span>
                  )}
                </span>
              ) : null
            }
          />
          <Row label="Alta" value={<DateCell value={supplier.createdAt} />} />
          <Row label="Actualizado" value={<DateCell value={supplier.updatedAt} />} />
          <Row label="Destinatario externo" value={supplier.externalRecipient?.email} />
        </Card>
        {supplier.notes && (
          <Card title="Notas" colSpan={2}>
            <p className="whitespace-pre-wrap">{supplier.notes}</p>
          </Card>
        )}
      </section>

      <section className="border rounded-md p-4 bg-card">
        <AttachmentsList
          resourceType="supplier"
          resourceId={id}
          readOnly={!canEdit}
          title="Documentación del proveedor"
          accept=".pdf,.doc,.docx,.zip,image/*"
        />
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-2">Evaluaciones</h2>

        {canEdit && (
          <form onSubmit={handleAddEval} className="flex flex-wrap items-end gap-3 border rounded-md p-3 mb-4 bg-muted/30">
            <div>
              <label className="block text-xs font-medium mb-1">Fecha</label>
              <input
                type="date"
                className="border rounded-md px-2 py-1.5 text-sm"
                {...register('evaluatedAt')}
              />
              {errors.evaluatedAt && <p className="text-xs text-destructive">{errors.evaluatedAt.message}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Puntaje (0–100)</label>
              <input
                type="number"
                step="0.1"
                min={0}
                max={100}
                className="border rounded-md px-2 py-1.5 text-sm w-24"
                {...register('score')}
              />
              {errors.score && <p className="text-xs text-destructive">{errors.score.message}</p>}
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium mb-1">Observaciones</label>
              <input
                type="text"
                className="border rounded-md px-2 py-1.5 text-sm w-full"
                {...register('notes')}
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-md px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isSubmitting ? 'Guardando…' : 'Agregar'}
            </button>
          </form>
        )}

        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Fecha</th>
                <th className="text-left px-3 py-2 font-medium">Puntaje</th>
                <th className="text-left px-3 py-2 font-medium">Evaluador</th>
                <th className="text-left px-3 py-2 font-medium">Observaciones</th>
              </tr>
            </thead>
            <tbody>
              {evalsLoading && (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">
                    Cargando…
                  </td>
                </tr>
              )}
              {!evalsLoading && evaluations.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">
                    Sin evaluaciones registradas
                  </td>
                </tr>
              )}
              {!evalsLoading &&
                evaluations.map((e) => (
                  <tr key={e.id} className="border-t">
                    <td className="px-3 py-2">
                      <DateCell value={e.evaluatedAt} />
                    </td>
                    <td className="px-3 py-2">{e.score.toFixed(1)}</td>
                    <td className="px-3 py-2">{e.evaluator?.email ?? '—'}</td>
                    <td className="px-3 py-2">{e.notes ?? '—'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Card({
  title,
  children,
  colSpan,
}: {
  title: string;
  children: React.ReactNode;
  colSpan?: 1 | 2;
}) {
  return (
    <div className={`border rounded-md p-4 ${colSpan === 2 ? 'md:col-span-2' : ''}`}>
      <h3 className="text-sm font-semibold uppercase text-muted-foreground mb-2">{title}</h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="col-span-2">{value || '—'}</span>
    </div>
  );
}
